import { Inject, Injectable } from '@angular/core';
import { CorrelationMatrixAPIService, Facet, GenericResponseSecurityCorrelationMatrix, PortfolioAnalysisAPIService, SecurityAnalysisAPIService } from '@blk/wealth-web-endpoint';
import { FacetResult } from '@blk/wealth-web-endpoint/model/facetResult';
import { GenericResponseTotalPortfolioAnalyticsResponseBody } from '@blk/wealth-web-endpoint/model/genericResponseTotalPortfolioAnalyticsResponseBody';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, WweFeature, WwePortfolioAnalysisServiceParams } from '@ddt-web/data-service';
import { ErrorObject, WweError } from '@ddt-web/error-handler';
import { StoreService } from '@ddt-web/store-service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { WweSecurityDetailsParams } from '../domain/objects/wwe-security-details-params.model';
import { WweSecuritySearchParams } from '../domain/objects/wwe-security-search-params.model';
import { WweSecurity } from '../domain/objects/wwe-security.model';
import { mapSecurityResolutionDetailsRequestToSecurityResolutionRequest } from '../domain/requests/security-details-request-mappings';
import { mapSecuritySearchRequestToSearchRequest } from '../domain/requests/security-search-request-mappings';
import { FilterRequest, SecuritySearchRequest } from '../domain/requests/security-search-request.model';
import { MultiResolveSecurityDetailsResponse } from '../domain/responses/multi-resolve-security-details-response.model';
import { mapMultiResolveSecurityDetailsResponseBodyToMultiResolveSecurityDetailsResponse, mapSecurityDetailsResponseBodyToSecurityDetailsResponse } from '../domain/responses/security-details-response-mappings';
import { SecurityDetailsResponse } from '../domain/responses/security-details-response.model';
import { mapSecuritySearchResponseToFacetFields, mapSecuritySearchResponseToWweSecurityList } from '../domain/responses/security-search-response-mappings';


@Injectable({
	providedIn: 'root'
})
export class WealthWebApiService {
	public static readonly POSITION_DETAILS_CONFIG_NAME = 'positionDetails';
	public static readonly TAX_DETAILS_CONFIG_NAME = 'taxDetails';
	public static readonly COMPARE_CONFIG_NAME = 'positionDetailsCompare';
	public static readonly TRANSIENT_POSITION_DETAILS_CONFIG_NAME = 'positionDetailsTransientAnalysis';

	@InjectConfig(SharedConfigKey.AWE_USE_ALADDINID) private readonly useAladdinId: boolean;
	public readonly CORRELATION_MATRIX: string = 'correlation-matrix';

	constructor(
		private portfolioAnalysisAPIService: PortfolioAnalysisAPIService,
		private securityAnalysisAPIService: SecurityAnalysisAPIService,
		private correlationMatrixAPIService: CorrelationMatrixAPIService,
		private storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	public fetchPortfolioAnalysis(apiParams: WwePortfolioAnalysisServiceParams): Observable<GenericResponseTotalPortfolioAnalyticsResponseBody> {
		return this.portfolioAnalysisAPIService.calculateMultiTotalPortfolio(
			apiParams.body,
			apiParams.rc,
			apiParams.analytics,
			apiParams.date,
			apiParams.pkg
		);
	}

	public securitySearch(securitySearchParams: WweSecuritySearchParams): Observable<WweSecurity[]> {
		return this.securityAnalysisAPIService.search(
			mapSecuritySearchRequestToSearchRequest(securitySearchParams.body),
			securitySearchParams.rc,
			securitySearchParams.date,
			securitySearchParams.pkg
		).pipe(map(mapSecuritySearchResponseToWweSecurityList));
	}

	public fetchFacets(securitySearchParams: WweSecuritySearchParams): Observable<FacetResult[]> {
		return this.securityAnalysisAPIService.search(
			mapSecuritySearchRequestToSearchRequest(securitySearchParams.body),
			securitySearchParams.rc,
			securitySearchParams.date,
			securitySearchParams.pkg
		).pipe(map(mapSecuritySearchResponseToFacetFields));
	}

	public fetchSecurityDetails(
		securityDetailsParams: WweSecurityDetailsParams, feature?: WweFeature, storeName?: string
	): Observable<SecurityDetailsResponse> {
		return this.securityAnalysisAPIService.details(
			mapSecurityResolutionDetailsRequestToSecurityResolutionRequest(securityDetailsParams.body, this.useAladdinId),
			securityDetailsParams.rc,
			securityDetailsParams.date,
			securityDetailsParams.pkg
		).pipe(
			catchError((error: WweError) => {
				if (this.storeConfig.positionDetails) {
					this.storeService.upsertMany(this.storeConfig.positionDetails, []);
				}

				if (feature === WweFeature.BUY_FUNDS) {
					return throwError(new ErrorObject(error, 'positionDetailsBuyFunds'));
				} else if (feature === WweFeature.COMPARE) {
					// this line will be hit when the compare api fail (400 or 500 code)
					return throwError(new ErrorObject(error, 'positionDetailsCompare'));
				} else if (feature === WweFeature.TRANSIENT_ANALYSIS) {
					return throwError(new ErrorObject(error, WealthWebApiService.TRANSIENT_POSITION_DETAILS_CONFIG_NAME));
				} else {
					const store = storeName ?? WealthWebApiService.POSITION_DETAILS_CONFIG_NAME;
					return throwError(new ErrorObject(error, store));
				}
			}),
			map(mapSecurityDetailsResponseBodyToSecurityDetailsResponse));
	}

	public fetchMultiResolveSecurityDetails(securityDetailsParams: WweSecurityDetailsParams): Observable<MultiResolveSecurityDetailsResponse> {
		return this.securityAnalysisAPIService.multiResolve(
			mapSecurityResolutionDetailsRequestToSecurityResolutionRequest(securityDetailsParams.body, this.useAladdinId),
			securityDetailsParams.rc,
			securityDetailsParams.date,
			securityDetailsParams.pkg
		).pipe(
			catchError((error: WweError) => {
				this.storeService.upsertMany(this.storeConfig.positionDetails, []);
				return throwError(new ErrorObject(error, WealthWebApiService.POSITION_DETAILS_CONFIG_NAME));
			}),
			map(mapMultiResolveSecurityDetailsResponseBodyToMultiResolveSecurityDetailsResponse));
	}

	public fetchCorrelationMatrix(fundIds: string[], currency: string, rc: string): Observable<GenericResponseSecurityCorrelationMatrix> {
		return this.correlationMatrixAPIService.calculateCorrelationMatrix(
			fundIds,
			currency,
			rc
		).pipe(catchError(error => {
			this.storeService.upsertMany(this.storeConfig.correlationMatrix, []);
			return throwError(new ErrorObject(error, this.CORRELATION_MATRIX));
		}));
	}

	public buildFilterRequest(field: string, values: string[]): FilterRequest {
		return {
			field: field,
			values: values
		} as FilterRequest;
	}

	public buildSecuritySearchRequest(query: string, filters: FilterRequest[] = null, facets: Facet[] = null): SecuritySearchRequest {
		return {
			query: query,
			filters: filters,
			facets: facets
		} as SecuritySearchRequest;
	}
}
